import React, { useState } from 'react';
import { ProfileMenu } from '../Profile/ProfileMenu';
import { useNavigate } from 'react-router-dom';
import { IoLogoWhatsapp } from 'react-icons/io5';
import Footer from '@/components/Footer';
import RoundVoting from './BossVoting';
import { FaCrown, FaFire } from 'react-icons/fa';
import { useGetVotingRoundsQuery } from '@/store/api/votingApi';
import VotingResults from './VotingResults';
import { buildAssetUrl } from '@/utils/asset';
import moment from 'moment';

type VotingCardProps = {
  title: string;
  date: string;
  time: string;
  icon: React.ReactNode;
  button?: React.ReactNode;
};

export interface VotingSession {
  id: number;
  title: string;
  description: string;
  banner: string | null;
  createdRound: number;
  startTime: string;
  endTime: string;
  status: string;
}

const formatDateParts = (date: string) => {
  const d = new Date(date);
  return {
    date: d.toDateString(),
    time: d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
  };
};

const VotingCard = ({ title, date, time, icon, button }: VotingCardProps) => (
  <div
    className="bg-[#000B05] border-2 border-[#00220E] rounded-xl px-6 py-6
    flex flex-col md:flex-row items-start md:items-stretch gap-6
    hover:-translate-y-1 transition-all duration-300
    shadow-[0_0_25px_rgba(0,255,150,0.1)]"
  >
    <div className="flex items-center gap-4 md:w-[320px]">
      <div className="text-5xl shrink-0">{icon}</div>

      <p className="text-2xl font-medium font-Gabarito line-clamp-2 break-words">{title}</p>
    </div>

    <div className="hidden md:block w-px bg-[#00220E] opacity-70" />

    <div className="flex flex-col justify-center md:w-[220px]">
      <div className="flex text-xl gap-2">
        <p className="text-[#E4FFEF]/50">Date:</p>
        <p>{date}</p>
      </div>
      <div className="flex text-xl gap-2">
        <p className="text-[#E4FFEF]/50">Time:</p>
        <p>{time}</p>
      </div>
    </div>

    <div className="hidden md:block w-px bg-[#00220E] opacity-70" />

    <div className="md:ml-auto flex items-center">{button}</div>
  </div>
);

export default function NewVotes() {
  const [isOpenVoting, setIsOpenVoting] = useState(false);
  const [resultSession, setResultSession] = useState<any>(null);
  const navigate = useNavigate();

  const { data, isLoading } = useGetVotingRoundsQuery();

  const sessions = (data?.data || []).map((s: VotingSession) => {
    const start = new Date(s.startTime);
    const end = new Date(s.endTime);

    return {
      ...s,
      image: s.banner,

      startTimeFormatted: `${start.toDateString()}, ${start.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      })}`,

      endTimeFormatted: `${end.toDateString()}, ${end.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      })}`,

      isActive: s.status === 'ongoing',
      isPast: s.status === 'closed',
      isUpcoming: s.status === 'upcomming',
    };
  });

  const activeSession = sessions
    .filter((s: any) => s.isActive)
    .sort((a: any, b: any) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime())[0];
  const upcomingSessions = sessions.filter((s: any) => s.isUpcoming);
  const pastSessions = sessions.filter((s: any) => s.isPast);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white">
        Loading Voting...
      </div>
    );
  }

  return (
    <>
      {resultSession ? (
        <VotingResults sessionId={resultSession.id} close={() => setResultSession(false)} />
      ) : isOpenVoting ? (
        <RoundVoting id={activeSession!.id} close={() => setIsOpenVoting(false)} />
      ) : (
        <>
          <div className="min-h-screen bg-[#05071A] relative overflow-hidden text-white">
            <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-[#FF5900] opacity-20 blur-[200px]" />
            <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#2C3FC2] opacity-20 blur-[220px]" />

            <header className="relative w-full border-b border-white/10 px-6 md:px-12 py-4 flex items-center justify-between">
              <img
                src="/images/logoWithText.svg"
                className="h-12 cursor-pointer"
                alt="logo"
                onClick={() => navigate('/')}
              />
              <ProfileMenu />
            </header>

            {activeSession && (
              <section className="relative z-10 flex flex-col items-center px-6 pt-20 pb-16">
                <h1 className="text-white text-3xl md:text-4xl font-semibold text-center mb-14 font-irish tracking-widest">
                  <span className="font-irish ">DECENT DEN –</span>{' '}
                  <span className="bg-white text-black px-4 py-1 rounded-md tracking-widest">
                    LIVE VOTING ARENA
                  </span>
                </h1>

                <div className="w-full max-w-5xl bg-[#010313] border border-[#3D4263] rounded-2xl px-6 md:px-12 py-8 md:py-10 flex flex-col md:flex-row items-center gap-10 shadow-[0_0_55px_rgba(53,61,121,0.55)] hover:-translate-y-4 transition-all duration-300">
                  <div className="flex justify-center md:w-1/2">
                    <img
                      src={buildAssetUrl(
                        activeSession.banner || activeSession.image,
                        '/images/KingImage.png'
                      )}
                      alt="Throne"
                      className="w-[180px] sm:w-[220px] md:w-[320px]"
                      onError={(e) => {
                        e.currentTarget.src = '/images/KingImage.png';
                      }}
                    />
                  </div>

                  <div className="text-center md:w-1/2">
                    <div className="inline-flex items-center gap-2 bg-[#FF6900] px-4 py-1 rounded-full text-sm mb-10">
                      <span className="w-2 h-2 bg-white rounded-full"></span>
                      Currently Active
                    </div>

                    <h2 className="text-3xl md:text-4xl font-medium mb-4">{activeSession.title}</h2>

                    <p className="text-[#CBD2FF]/60 mb-6">{activeSession.description}</p>

                    <div className="flex items-center justify-center gap-10 my-10 text-lg">
                      <div>
                        <p className="text-[#CBD2FF]/60">Start:</p>
                        <p>{activeSession.startTimeFormatted?.split(',')[0]}</p>
                        <p>{activeSession.startTimeFormatted?.split(',')[1]}</p>
                      </div>

                      <div className="h-8 w-[1px] bg-gray-600"></div>

                      <div>
                        <p className="text-[#CBD2FF]/60">End:</p>
                        <p>{activeSession.endTimeFormatted?.split(',')[0]}</p>
                        <p>{activeSession.endTimeFormatted?.split(',')[1]}</p>
                      </div>
                    </div>

                    <button
                      onClick={() => setIsOpenVoting(true)}
                      className="bg-white text-black px-10 py-3 rounded-md w-full font-medium hover:bg-gray-200 transition"
                    >
                      Vote Now
                    </button>
                  </div>
                </div>
              </section>
            )}

            <section className="w-full px-4 sm:px-6 py-20 md:py-40 flex flex-col items-center">
              <h2 className="text-3xl md:text-4xl font-semibold mb-12 text-center font-irish tracking-widest">
                UPCOMING{' '}
                <span className="bg-white text-black px-4 py-1 rounded-md tracking-widest">
                  VOTING ROUNDS
                </span>
              </h2>

              <div className="w-full max-w-5xl flex flex-col gap-8">
                {upcomingSessions.length === 0 ? (
                  <div className="text-center text-white/60 text-lg py-10">
                    No upcoming voting sessions
                  </div>
                ) : (
                  upcomingSessions.map((session: any) => {
                    const date = moment(session.startTime).format('DD MMM YYYY');
                    const time = moment(session.startTime).format('hh:mm A');
                    return (
                      <VotingCard
                        key={session.id}
                        title={session.title}
                        date={date}
                        time={time}
                        icon={<FaFire className="text-[#FF7A33]" />}
                      />
                    );
                  })
                )}
              </div>
            </section>
            <section className="w-full px-4 sm:px-6 pb-20 md:pb-40 flex flex-col items-center">
              <h2 className="text-3xl md:text-4xl font-semibold mb-12 text-center font-irish tracking-widest">
                Past{' '}
                <span className="bg-white text-black px-4 py-1 rounded-md tracking-wides">
                  VOTING ROUNDS
                </span>
              </h2>

              <div className="w-full max-w-5xl flex flex-col gap-8">
                {pastSessions.length === 0 ? (
                  <div className="text-center text-white/60 text-lg py-10">
                    No past voting sessions
                  </div>
                ) : (
                  pastSessions.map((session: any) => {
                    const date = moment(session.startTime).format('DD MMM YYYY');
                    const time = moment(session.startTime).format('hh:mm A');
                    return (
                      <VotingCard
                        key={session.id}
                        title={session.title}
                        date={date}
                        time={time}
                        icon={<FaCrown className="text-[#FF7A33]" />}
                        button={
                          <button
                            onClick={() => setResultSession(session)}
                            className="bg-white text-black px-6 py-2 rounded-md text-xl hover:bg-gray-200 transition"
                          >
                            Check Results
                          </button>
                        }
                      />
                    );
                  })
                )}
              </div>
            </section>
          </div>
        </>
      )}
      <Footer />
    </>
  );
}
